import { Route, RouteConfig } from 'vue-router';
import { addFunds } from '@src/store/modules/add-funds';
import { AddFundsUpgradeRoute } from 'types/add-funds';
import { addFundsUpgradeRouteOrder } from '@src/constants';
import { app } from '@store/modules/app';
import { getCurrentRiaPlan } from '@api/investment-entity';
import { investmentEntity } from '@src/store/modules/investment-entity';
import { upgrade } from '@store/modules/upgrade';

const getFlowStepIndex = (name: AddFundsUpgradeRoute): number => {
	return addFundsUpgradeRouteOrder.indexOf(name) + 1;
};

const metaConstructor = (route: Route): object => ({
	step: getFlowStepIndex(route.name as AddFundsUpgradeRoute)
});

const upgradeRequiresCorePlanSelection = (): boolean => {
	const currentLevelBelowCore =
		investmentEntity.currentServiceLevel.name === 'STARTER' ||
		investmentEntity.currentServiceLevel.name === 'BASIC';
	const upgradeCoreOrHigher =
		upgrade.upgradeLevelName === 'CORE' ||
		upgrade.upgradeLevelName === 'ADVANCED' ||
		upgrade.upgradeLevelName === 'PREMIUM';
	return currentLevelBelowCore && upgradeCoreOrHigher;
};

const upgradeRequiresPlusPlanSelection = (): boolean => {
	const currentLevelBelowAdvanced =
		investmentEntity.currentServiceLevel.name === 'STARTER' ||
		investmentEntity.currentServiceLevel.name === 'BASIC' ||
		investmentEntity.currentServiceLevel.name === 'CORE';
	const upgradeAdvancedOrHigher = upgrade.upgradeLevelName === 'ADVANCED' || upgrade.upgradeLevelName === 'PREMIUM';
	return currentLevelBelowAdvanced && upgradeAdvancedOrHigher;
};

/* eslint-disable @typescript-eslint/explicit-function-return-type */
const upgradeRoutes: Array<RouteConfig> = [
	{
		path: `/account/upgrade`,
		name: 'upgrade',
		component: () => import(/* webpackChunkName: "upgrade" */ '@views/account/upgrade/upgrade.vue'),
		beforeEnter: async (to, from, next) => {
			if (!upgrade.dataLoaded) {
				await upgrade.fetchAndStoreServiceLevelUpgradeData();
			}

			next();
		},
		redirect: { name: 'upgrade-landing' },
		children: [
			{
				path: 'start',
				name: 'upgrade-landing',
				meta: metaConstructor,
				beforeEnter: async (to, from, next) => {
					if (!upgrade.hasAvailableNextLevel) {
						next({ name: 'account-overview', replace: true });
						app.ADD_TOAST({
							type: 'error',
							message: 'The current account is not eligible for an upgrade at this time.'
						});
					} else {
						next();
					}
				},
				// Add route guards to below pages enforcing visiting start first for UPGRADE flow (not INVEST-UPGRADE)
				component: () =>
					import(/* webpackChunkName: "upgrade-start" */ '@views/account/upgrade/upgrade-start.vue')
			},
			{
				path: 'select-core-plan',
				name: 'upgrade-core-plans',
				meta: metaConstructor,
				beforeEnter: async (to, from, next) => {
					if (investmentEntity.isIraEntity) {
						next({ name: 'account-overview', replace: true });
					} else if (!upgrade.dataLoaded || !upgradeRequiresCorePlanSelection()) {
						next({ name: 'upgrade-landing', replace: true });
					} else if (!upgrade.aboveUpgradeThreshold && !addFunds.investUpgrade) {
						next({ name: 'add-funds-ria-amount', replace: true });
					} else {
						next();
					}
				},
				component: () =>
					import(/* webpackChunkName: "upgrade-core-plans" */ '@views/account/upgrade/upgrade-core-plans.vue')
			},
			{
				path: 'plus-plan',
				name: 'upgrade-plus-plans',
				meta: metaConstructor,
				beforeEnter: async (to, from, next) => {
					const entryAllowedToChangePlusPlan =
						!investmentEntity.isIraEntity &&
						(investmentEntity.currentServiceLevel.name === 'ADVANCED' ||
							investmentEntity.currentServiceLevel.name === 'PREMIUM');
					if (entryAllowedToChangePlusPlan) {
						const currentPlanIsAlreadyPlus = (await getCurrentRiaPlan()).planType === 'STANDARD_PLUS';
						if (currentPlanIsAlreadyPlus) {
							next({ name: 'account-overview', replace: true });
						} else {
							next();
						}
					} else if (investmentEntity.isIraEntity) {
						next({ name: 'account-overview', replace: true });
					} else if (!upgrade.dataLoaded || !upgradeRequiresPlusPlanSelection()) {
						next({ name: 'upgrade-landing', replace: true });
					} else if (!upgrade.aboveUpgradeThreshold && !addFunds.investUpgrade) {
						addFunds.UPDATE_FORCE_INVEST_UPGRADE(true);
						next({ name: 'add-funds-ria-amount', replace: true });
					} else {
						next();
					}
				},
				component: () =>
					import(
						/* webpackChunkName: "upgrade-plus-plans" */ '@views/account/upgrade/upgrade-plus-plans-non-ira.vue'
					)
			},
			{
				path: 'accreditation-information',
				name: 'upgrade-accreditation',
				meta: metaConstructor,
				beforeEnter: async (to, from, next) => {
					if (!upgrade.premiumShouldSeeAccreditationPage) {
						next({ name: 'account-overview', replace: true });
					} else if (!upgrade.dataLoaded || upgrade.upgradeLevelName !== 'PREMIUM') {
						next({ name: 'upgrade-landing', replace: true });
					} else if (!upgrade.aboveUpgradeThreshold && !addFunds.investUpgrade) {
						next({ name: 'add-funds-ria-amount', replace: true });
					} else {
						next();
					}
				},
				component: () =>
					import(
						/* webpackChunkName: "upgrade-accreditation" */ '@views/account/upgrade/upgrade-accreditation.vue'
					)
			},
			{
				path: 'view-allocation',
				name: 'upgrade-view-allocation',
				meta: metaConstructor,
				beforeEnter: async (to, from, next) => {
					if (!upgrade.newRiaPlan?.id || !upgrade.upgradeLevelName || !addFunds.amount) {
						next({ name: 'add-funds-ria-amount', replace: true });
					} else if (upgrade.upgradeLevelName === 'PREMIUM') {
						next({ name: 'account-overview', replace: true });
					} else {
						next();
					}
				},
				component: () =>
					import(
						/* webpackChunkName: "upgrade-view-allocation" */ '@views/account/upgrade/upgrade-view-allocation.vue'
					)
			},
			{
				path: 'success',
				name: 'upgrade-success',
				meta: metaConstructor,
				component: () =>
					import(/* webpackChunkName: "upgrade-success" */ '@views/account/upgrade/upgrade-success.vue')
			}
		]
	}
];

export default upgradeRoutes;
